import type { GameStateManager } from '../game/GameState';
import type { GameData, LocationId, GameSpeed, ResearcherData, OrderType } from '../game/types';
import type { RealityBreachSystem } from '../systems/RealityBreachSystem';
import { ResearcherPanel } from './ResearcherPanel';
import { CellDisplay } from './CellDisplay';
import { ContextWindow } from './ContextWindow';
import { NinePadDial } from './NinePadDial';
import { ResourcePanel } from './ResourcePanel';
import { SpeedControls } from './SpeedControls';
import { ResearchModal } from './ResearchModal';
import { SilverBulletModal } from './SilverBulletModal';
import { RecruitModal } from './RecruitModal';
import { MenuModal } from './MenuModal';
import { ResearchCompletionModal } from './ResearchCompletionModal';
import { BreachEscapeModal } from './BreachEscapeModal';
import { BreachContainmentModal } from './BreachContainmentModal';
import { AnomalyReceivedModal } from './AnomalyReceivedModal';
import { InsanityModal } from './InsanityModal';
import { RealityBreachModal } from './RealityBreachModal';
import { RealityKillModal } from './RealityKillModal';
import { EffectsDisplay } from './EffectsDisplay';
import { ToastManager } from './ToastManager';
import { HotkeyManager } from './HotkeyManager';
import { HowToPlayModal } from './HowToPlayModal';
import { ControlsModal } from './ControlsModal';
import { CompendiumModal } from './CompendiumModal';
import { ScientistSelectionModal } from './ScientistSelectionModal';
import { WinModal } from './WinModal';
import { LoseModal } from './LoseModal';
import { AudioManager } from '../audio/AudioManager';
import { TooltipManager } from './TooltipManager';
import { ObjectiveLog } from './ObjectiveLog';
import { getSpeedMultiplier } from '../utils/GameUtils';
import { CELL_LOCATIONS } from '../utils/Constants';
import { ResearcherTransitManager } from './managers/ResearcherTransitManager';
import { PendingStateHandler } from './managers/PendingStateHandler';
import { ModalCoordinator } from './managers/ModalCoordinator';

export class UIManager {
  private researcherPanel: ResearcherPanel;
  private cellDisplays: Map<LocationId, CellDisplay>;
  private contextWindow: ContextWindow;
  private ninePadDial: NinePadDial;
  private resourcePanel: ResourcePanel;
  private speedControls: SpeedControls;
  private researchModal: ResearchModal;
  private silverBulletModal: SilverBulletModal;
  private recruitModal: RecruitModal;
  private menuModal: MenuModal;
  private researchCompletionModal: ResearchCompletionModal;
  private breachEscapeModal: BreachEscapeModal;
  private breachContainmentModal: BreachContainmentModal;
  private anomalyReceivedModal: AnomalyReceivedModal;
  private insanityModal: InsanityModal;
  private realityBreachModal: RealityBreachModal | null = null;
  private realityKillModal: RealityKillModal;
  private howToPlayModal: HowToPlayModal;
  private controlsModal: ControlsModal;
  private compendiumModal: CompendiumModal;
  private scientistSelectionModal: ScientistSelectionModal;
  private winModal: WinModal;
  private loseModal: LoseModal;
  private effectsDisplay: EffectsDisplay;
  private toastManager: ToastManager;
  private hotkeyManager: HotkeyManager;
  private objectiveLog: ObjectiveLog;
  private _tooltipManager: TooltipManager;
  private audioManager: AudioManager;
  private container: HTMLElement;
  private gameState: GameStateManager;
  private gameData: GameData;
  private onOrder: (order: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest', ...args: unknown[]) => void;
  private onSpeedChange: (speed: GameSpeed) => void;
  private pendingMoveMode: boolean = false;
  private transitManager: ResearcherTransitManager;
  private pendingStateHandler: PendingStateHandler;
  private modalCoordinator: ModalCoordinator;
  private lastUpdateTime: number = performance.now();
  private realityBreachSystem: RealityBreachSystem | null = null;

  getToastManager(): ToastManager {
    return this.toastManager;
  }

  constructor(
    container: HTMLElement,
    gameState: GameStateManager,
    gameData: GameData,
    onOrder: (order: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest', ...args: unknown[]) => void,
    onSpeedChange: (speed: 'pause' | '1x' | '2x' | '4x') => void
  ) {
    this.gameState = gameState;
    this.gameData = gameData;
    this.onOrder = onOrder;
    this.onSpeedChange = onSpeedChange;
    this.container = container;
    this.audioManager = AudioManager.getInstance();
    this.setupContainer();

    this.researcherPanel = new ResearcherPanel(
      this.container.querySelector('#researcher-panel')!,
      gameState,
      gameData,
      (id) => this.selectResearcher(id)
    );

    this.cellDisplays = new Map();
    const locations: LocationId[] = ['cell_1', 'cell_2', 'control_room', 'cell_3', 'cell_4'];
    locations.forEach(locationId => {
      const element = this.container.querySelector(`#cell-${locationId}`) as HTMLElement;
      if (element) {
        this.cellDisplays.set(locationId, new CellDisplay(
          element,
          locationId,
          gameState,
          gameData,
          (id) => this.selectLocation(id),
          (id) => this.selectResearcher(id),
          (id) => this.handleMoveOrder(id)
        ));
      }
    });

    this.contextWindow = new ContextWindow(
      this.container.querySelector('#context-window')!,
      gameState,
      gameData
    );

    this.ninePadDial = new NinePadDial(
      this.container.querySelector('#nine-pad-dial')!,
      gameState,
      gameData,
      (action, ...args) => {
        if (action === 'deselect') {
          this.gameState.setSelectedEntity(null, null);
          this.update();
        } else {
          this.handleAction(action, ...args);
        }
      }
    );

    this.resourcePanel = new ResourcePanel(
      this.container.querySelector('#resource-panel')!,
      gameState,
      gameData
    );

    this.speedControls = new SpeedControls(
      this.container.querySelector('#speed-controls')!,
      gameState,
      gameData,
      onSpeedChange
    );

    this.researchModal = new ResearchModal(
      this.container.querySelector('#research-modal')!,
      gameState,
      gameData,
      (researcherId, types, cellId) => {
        const restoredSpeed = this.gameState.restorePauseStateAfterModal();
        if (restoredSpeed !== null) {
          onSpeedChange(restoredSpeed);
        }
        onOrder('research', researcherId, types, cellId);
      },
      () => {
        const restoredSpeed = this.gameState.restorePauseStateAfterModal();
        if (restoredSpeed !== null) {
          onSpeedChange(restoredSpeed);
        }
      },
      onSpeedChange
    );

    this.silverBulletModal = new SilverBulletModal(
      this.container.querySelector('#silver-bullet-modal')!,
      gameState,
      gameData,
      (bulletId) => {
        const restoredSpeed = this.gameState.restorePauseStateAfterModal();
        if (restoredSpeed !== null) {
          onSpeedChange(restoredSpeed);
        }
        onOrder('craft', bulletId);
      },
      () => {
        const restoredSpeed = this.gameState.restorePauseStateAfterModal();
        if (restoredSpeed !== null) {
          onSpeedChange(restoredSpeed);
        }
      },
      onSpeedChange
    );

    this.recruitModal = new RecruitModal(
      this.container.querySelector('#recruit-modal')!,
      gameState,
      gameData,
      () => {
        const restoredSpeed = this.gameState.restorePauseStateAfterModal();
        if (restoredSpeed !== null) {
          onSpeedChange(restoredSpeed);
        }
        onOrder('recruit');
      },
      () => {
        const restoredSpeed = this.gameState.restorePauseStateAfterModal();
        if (restoredSpeed !== null) {
          onSpeedChange(restoredSpeed);
        }
      },
      onSpeedChange
    );

    this.howToPlayModal = new HowToPlayModal(
      this.container.querySelector('#how-to-play-modal')!,
      gameState,
      gameData,
      onSpeedChange
    );

    this.controlsModal = new ControlsModal(
      this.container.querySelector('#controls-modal')!,
      gameState,
      gameData,
      onSpeedChange
    );

    this.compendiumModal = new CompendiumModal(
      this.container.querySelector('#compendium-modal')!,
      gameState,
      gameData,
      onSpeedChange
    );

    this.scientistSelectionModal = new ScientistSelectionModal(
      this.container.querySelector('#scientist-selection-modal')!,
      gameState,
      gameData
    );

    this.winModal = new WinModal(
      this.container.querySelector('#win-modal')!,
      gameState,
      gameData,
      async () => {
        // New Game button - clear game end state first to prevent modal from re-appearing
        // Then show scientist selection and restart
        this.gameState.clearGameEndState();
        const selectedScientists = await this.showScientistSelection();
        this.gameState.restart(selectedScientists);
        this.update();
      }
    );

    this.loseModal = new LoseModal(
      this.container.querySelector('#lose-modal')!,
      gameState,
      gameData,
      async () => {
        // New Game button - clear game end state first to prevent modal from re-appearing
        // Then show scientist selection and restart
        this.gameState.clearGameEndState();
        const selectedScientists = await this.showScientistSelection();
        this.gameState.restart(selectedScientists);
        this.update();
      }
    );

    this.menuModal = new MenuModal(
      this.container.querySelector('#menu-modal')!,
      gameState,
      gameData,
      () => {
        // MenuModal handles pause state restoration in its hide() method
      },
      () => {
        this.controlsModal.show();
      },
      () => {
        this.howToPlayModal.show();
      },
      () => {
        this.compendiumModal.show();
      },
      onSpeedChange
    );

    this.researchCompletionModal = new ResearchCompletionModal(
      this.container.querySelector('#research-completion-modal')!,
      gameState,
      gameData,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      onSpeedChange
    );

    this.breachEscapeModal = new BreachEscapeModal(
      this.container.querySelector('#breach-escape-modal')!,
      gameState,
      gameData,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      onSpeedChange
    );

    this.breachContainmentModal = new BreachContainmentModal(
      this.container.querySelector('#breach-containment-modal')!,
      gameState,
      gameData,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      onSpeedChange
    );

    this.anomalyReceivedModal = new AnomalyReceivedModal(
      this.container.querySelector('#anomaly-received-modal')!,
      gameState,
      gameData,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      onSpeedChange
    );

    this.insanityModal = new InsanityModal(
      this.container.querySelector('#insanity-modal')!,
      gameState,
      gameData,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      onSpeedChange
    );

    this.realityKillModal = new RealityKillModal(
      this.container.querySelector('#reality-kill-modal')!,
      gameState,
      gameData,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      onSpeedChange
    );

    this.effectsDisplay = new EffectsDisplay(
      this.container.querySelector('#effects-display')!,
      gameState,
      gameData
    );

    this.objectiveLog = new ObjectiveLog(
      this.container.querySelector('#objective-log')!,
      gameState,
      gameData
    );

    this.toastManager = new ToastManager(
      this.container.querySelector('#toast-container')!,
      gameData
    );

    this._tooltipManager = new TooltipManager(
      this.container,
      gameData
    );

    // Initialize managers
    this.transitManager = new ResearcherTransitManager(
      this.container,
      this.gameState,
      this.gameData,
      (id) => this.selectResearcher(id)
    );

    this.pendingStateHandler = new PendingStateHandler(
      this.gameState,
      this.researchCompletionModal,
      this.breachEscapeModal,
      this.breachContainmentModal,
      this.anomalyReceivedModal,
      this.insanityModal,
      this.realityBreachModal,
      this.realityKillModal,
      this.toastManager
    );

    this.modalCoordinator = new ModalCoordinator(
      this.researchModal,
      this.silverBulletModal,
      this.recruitModal,
      this.menuModal,
      this.howToPlayModal,
      this.controlsModal,
      this.compendiumModal
    );

    // Attach restart button handler
    const restartButton = this.container.querySelector('#restart-button');
    if (restartButton) {
      restartButton.addEventListener('click', async () => {
        this.audioManager.playClick();
        // Close all modals before restarting
        this.modalCoordinator.closeAllModals();
        if (this.researchCompletionModal.isOpen()) this.researchCompletionModal.hide();
        if (this.breachEscapeModal.isOpen()) this.breachEscapeModal.hide();
        if (this.breachContainmentModal.isOpen()) this.breachContainmentModal.hide();
        if (this.anomalyReceivedModal.isOpen()) this.anomalyReceivedModal.hide();
        if (this.insanityModal.isOpen()) this.insanityModal.hide();
        if (this.realityBreachModal?.isOpen()) this.realityBreachModal.hide();
        if (this.realityKillModal.isOpen()) this.realityKillModal.hide();
        if (this.winModal.isOpen()) this.winModal.hide();
        if (this.loseModal.isOpen()) this.loseModal.hide();
        
        // Show scientist selection modal
        const selectedScientists = await this.showScientistSelection();
        
        // Reset game state with selected scientists
        this.gameState.restart(selectedScientists);
        
        // Clear any pending move mode
        this.pendingMoveMode = false;
        
        // Update UI
        this.update();
      });
    }

    // Attach menu button handler
    const menuButton = this.container.querySelector('#menu-button');
    if (menuButton) {
      menuButton.addEventListener('click', () => {
        this.audioManager.playClick();
        this.menuModal.show();
      });
    }

    // Initialize hotkey manager
    this.hotkeyManager = new HotkeyManager(this.gameData, {
      onNinePadClick: (index) => {
        this.ninePadDial.clickButton(index);
      },
      onNextResearcher: () => {
        this.researcherPanel.selectNextResearcher();
        this.update();
      },
      onMenuToggle: () => {
        if (this.modalCoordinator.isModalOpen('menu')) {
          this.menuModal.hide();
        } else {
          this.menuModal.show();
        }
      },
      onPauseToggle: () => {
        const isPaused = this.gameState.isPaused();
        this.gameState.setPaused(!isPaused);
        if (!isPaused) {
          this.gameState.setGameSpeed('pause');
        } else {
          this.gameState.setGameSpeed('1x');
        }
        this.update();
      },
      onSpeedChange: (speed) => {
        this.speedControls.setSpeed(speed);
        this.onSpeedChange(speed);
        this.update();
      },
      onMoveToLocation: (location) => {
        this.handleMoveOrder(location);
        this.pendingMoveMode = false;
      },
      onModalSelect: (index) => {
        // This will be handled by modal's own keyboard handlers
        // But we can also trigger it here if needed
        if (this.modalCoordinator.isModalOpen('research')) {
          // Research modal handles its own selection
        } else if (this.modalCoordinator.isModalOpen('silverBullet')) {
          // Silver bullet modal handles its own selection
        }
      },
      onModalConfirm: () => {
        // Modals handle their own confirmation
      },
      isModalOpen: () => {
        return this.modalCoordinator.isAnyModalOpen();
      },
      isMoveModeActive: () => {
        return this.pendingMoveMode;
      },
      getCurrentSpeed: () => {
        return this.gameState.getGameSpeed();
      },
      getNextSpeed: () => {
        return this.speedControls.getNextSpeed();
      },
      getPreviousSpeed: () => {
        return this.speedControls.getPreviousSpeed();
      }
    });
  }


  private setupContainer(): void {
    this.container.innerHTML = `
      <div id="resource-panel">
        <div id="speed-controls"></div>
        <button id="restart-button">Restart</button>
        <button id="menu-button">Menu</button>
      </div>
      <div id="researcher-panel"></div>
      <div id="cells-container">
        <div id="objective-log"></div>
        <div id="cell-cell_1"></div>
        <div id="cell-cell_2"></div>
        <div id="corridor-cell_1-cell_2" class="corridor corridor-horizontal" data-from="cell_1" data-to="cell_2"></div>
        <div id="corridor-cell_2-control_room" class="corridor corridor-vertical" data-from="cell_2" data-to="control_room"></div>
        <div id="cell-control_room"></div>
        <div id="corridor-control_room-cell_3" class="corridor corridor-vertical" data-from="control_room" data-to="cell_3"></div>
        <div id="cell-cell_3"></div>
        <div id="corridor-cell_3-cell_4" class="corridor corridor-horizontal" data-from="cell_3" data-to="cell_4"></div>
        <div id="cell-cell_4"></div>
      </div>
      <div id="effects-display"></div>
      <div id="nine-pad-dial"></div>
      <div id="context-window"></div>
      <div id="research-modal" class="modal hidden"></div>
      <div id="silver-bullet-modal" class="modal hidden"></div>
      <div id="recruit-modal" class="modal hidden"></div>
      <div id="menu-modal" class="modal hidden"></div>
      <div id="how-to-play-modal" class="modal hidden"></div>
      <div id="controls-modal" class="modal hidden"></div>
      <div id="compendium-modal" class="modal hidden"></div>
      <div id="research-completion-modal" class="modal hidden"></div>
      <div id="breach-escape-modal" class="modal hidden"></div>
      <div id="breach-containment-modal" class="modal hidden"></div>
      <div id="anomaly-received-modal" class="modal hidden"></div>
      <div id="insanity-modal" class="modal hidden"></div>
      <div id="reality-breach-modal" class="modal hidden"></div>
      <div id="reality-kill-modal" class="modal hidden"></div>
      <div id="scientist-selection-modal" class="modal hidden"></div>
      <div id="win-modal" class="modal hidden"></div>
      <div id="lose-modal" class="modal hidden"></div>
      <div id="toast-container"></div>
    `;
  }

  showScientistSelection(): Promise<ResearcherData[]> {
    return new Promise((resolve) => {
      this.scientistSelectionModal.show((scientists) => {
        resolve(scientists);
      });
    });
  }

  private selectResearcher(id: string): void {
    const researcher = this.gameState.getResearcher(id);
    if (!researcher) {
      return; // Validate researcher exists
    }
    this.gameState.setSelectedEntity('researcher', id);
    this.update();
  }

  private selectLocation(id: LocationId): void {
    this.gameState.setSelectedEntity('location', id);
    this.update();
  }

  private handleMoveOrder(targetLocation: LocationId): void {
    const selected = this.gameState.getSelectedEntity();
    if (selected.type === 'researcher') {
      const researcher = this.gameState.getResearcher(selected.id as string);
      if (researcher) {
        this.onOrder('move', targetLocation);
        this.pendingMoveMode = false;
        this.update();
      }
    }
  }

  private handleAction(action: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest', ...args: unknown[]): void {
    const selected = this.gameState.getSelectedEntity();
    if (selected.type === 'researcher') {
      const researcher = this.gameState.getResearcher(selected.id as string);
      if (!researcher) {
        // Researcher no longer exists, clear selection
        this.gameState.setSelectedEntity(null, null);
        this.update();
        return;
      }

      if (action === 'move') {
        // Enter move mode - wait for target selection
        this.pendingMoveMode = true;
        this.update();
      } else if (action === 'research') {
        // Find the cell with anomaly for this researcher
        const researchCell = CELL_LOCATIONS.find(cell => {
          const anomaly = this.gameState.getAnomalyInCell(cell);
          return anomaly && researcher.location === cell;
        });
        if (researchCell) {
          this.researchModal.show(researcher, researchCell);
        }
      } else if (action === 'craft') {
        this.silverBulletModal.show(researcher);
      } else if (action === 'recruit') {
        this.recruitModal.show(researcher);
      } else {
        this.onOrder(action, ...args);
        // Update UI after order is issued
        this.update();
      }
    } else {
      this.onOrder(action, ...args);
      this.update();
    }
  }

  update(): void {
    const now = performance.now();
    this.researcherPanel.update();
    this.cellDisplays.forEach(display => display.update());
    this.transitManager.update(now);
    this.contextWindow.update();
    this.ninePadDial.update();
    this.resourcePanel.update();
    this.speedControls.update();
    
    // Check for pending modals and toasts
    this.pendingStateHandler.checkAndShowPendingModals();
    
    // Update effects display
    this.effectsDisplay.update();
    
    // Update objective log
    this.objectiveLog.update();
    
    this.lastUpdateTime = now;
  }

  setRealityBreachSystem(system: RealityBreachSystem): void {
    this.realityBreachSystem = system;
    // Initialize reality breach modal now that we have the system
    this.realityBreachModal = new RealityBreachModal(
      this.container.querySelector('#reality-breach-modal')!,
      this.gameState,
      this.gameData,
      system,
      () => {
        this.pendingStateHandler.notifyModalClosed();
        this.update();
      },
      this.onSpeedChange
    );
    // Update pending state handler with new modal
    this.pendingStateHandler = new PendingStateHandler(
      this.gameState,
      this.researchCompletionModal,
      this.breachEscapeModal,
      this.breachContainmentModal,
      this.anomalyReceivedModal,
      this.insanityModal,
      this.realityBreachModal,
      this.realityKillModal,
      this.winModal,
      this.loseModal,
      this.toastManager
    );
  }




}

